import { components } from 'src/app/services/api/lna-api-v2.schema';

// Steps
export enum InformationRequestStep {
  Request = 'Request',
  ClientAndPayer = 'ClientAndPayer',
  Summary = 'Summary'
}

// Enums
export type InformationRequestTopicType = components['schemas']['InformationRequestTopicType'];
export type InformationRequestPrintView = components['schemas']['InformationRequestPrintView'];
export type PrintSection = components['schemas']['PrintSection'];
export type PrintBlock = components['schemas']['PrintBlock'];
export type PrintLine = components['schemas']['PrintLine'];
export type PrintDocument = components['schemas']['PrintDocument'];
export type RequesterRelationType = components['schemas']['RequesterRelationType'];
export type RequesterForOtherPersonType = components['schemas']['RequesterForOtherPersonType'];
export type PayerType = components['schemas']['PayerType'];
export type DiscountGroupType = components['schemas']['DiscountGroupType'];
export type ServiceDeadlineType = components['schemas']['ServiceDeadlineType'];
export type ResponseDeliveryMethod = components['schemas']['ResponseDeliveryMethod'];
export type InvoiceDeliveryMethod = components['schemas']['InvoiceDeliveryMethod'];
export type InformationRequestPurpose = components['schemas']['InformationRequestPurpose'];
export type InformationRequestServiceType = components['schemas']['InformationRequestServiceType'];
export type SubmissionChannel = components['schemas']['SubmissionChannel'];
export type InformationRequestBackofficeStatus = components['schemas']['InformationRequestBackofficeStatus'];
export type InformationRequestFrontofficeStatus = components['schemas']['InformationRequestFrontofficeStatus'];

// Topic and field definitions (matches real API schema)
export interface InformationRequestTopicResponse {
  topicType: InformationRequestTopicType;
  name: string;
  description?: string | null;
  isVsaaEligible: boolean;
  maxBlocks: number;
  blockLabel?: string | null;
  versionId: string;
  fields: TopicFieldDefinitionResponse[];
}

export interface TopicFieldDefinitionResponse {
  fieldKey: string;
  fieldLabel: string;
  fieldType: string;
  maxLength?: number | null;
  isRequired: boolean;
  isConditional: boolean;
  conditionalOn?: string | null;
  conditionalValue?: string | null;
  blockIndex: number;
  sortOrder: number;
  sectionTitle?: string | null;
  tooltipText?: string | null;
}

// API Request/Response types from schema
export type CreateBoInformationRequestRequest = components['schemas']['CreateBoInformationRequestRequest'] & {
  serviceDeadlineDate?: string | null;
  invoiceDeliveryStructuralUnitId?: string | null;
  responseDeliveryStructuralUnitId?: string | null;
};
export type UpdateBoInformationRequestRequest = components['schemas']['UpdateBoInformationRequestRequest'] & {
  serviceDeadlineDate?: string | null;
  invoiceDeliveryStructuralUnitId?: string | null;
  responseDeliveryStructuralUnitId?: string | null;
};
export type BackofficeInformationRequestCard = components['schemas']['BackofficeInformationRequestCard'] & {
  serviceDeadlineDate?: string | null;
  invoiceDeliveryStructuralUnitId?: string | null;
  invoiceDeliveryStructuralUnitName?: string | null;
  responseDeliveryStructuralUnitId?: string | null;
  responseDeliveryStructuralUnitName?: string | null;
  contactPersonName?: string | null;
  contactPersonSurname?: string | null;
  contactPersonEmail?: string | null;
  contactPersonPhone?: string | null;
  prepaymentApplies?: boolean | null;
  postpaymentApplies?: boolean | null;
  noChargeApplies?: boolean | null;
};
export type InformationRequestClientInfoItem = components['schemas']['InformationRequestClientInfoItem'];

// Legacy aliases for backward compatibility during migration
export type InformationRequestCard = BackofficeInformationRequestCard;

// Action request types
export type StartPreliminaryResearchRequest = components['schemas']['StartPreliminaryResearchRequest'];
export type StartProcessingRequest = components['schemas']['StartProcessingRequest'];
export type CompleteInformationRequestRequest = components['schemas']['CompleteInformationRequestRequest'];
export type CancelBoInformationRequestRequest = components['schemas']['CancelBoInformationRequestRequest'];
export type ForwardInformationRequestRequest = components['schemas']['ForwardInformationRequestRequest'];
export type HandOverInformationRequestRequest = components['schemas']['HandOverInformationRequestRequest'];

// Response tab types
export type ResponseDocumentTypeItem = components['schemas']['ResponseDocumentTypeItem'];
export type ResponseDocumentTypeItemRequest = components['schemas']['ResponseDocumentTypeItemRequest'];
export type SetResponseDocumentTypesRequest = components['schemas']['SetResponseDocumentTypesRequest'];
export type NamejsDocumentItem = components['schemas']['NamejsDocumentItem'];
export type NamejsDocumentKind = components['schemas']['NamejsDocumentKind'];
export type NamejsDocumentStatus = components['schemas']['NamejsDocumentStatus'];
export type OtherResponseItem = components['schemas']['OtherResponseItem'];
export type OtherResponseRequest = components['schemas']['OtherResponseRequest'];
export type ResponseDraftItem = components['schemas']['ResponseDraftItem'];
export type ResponseDraftRequest = components['schemas']['ResponseDraftRequest'];
export type SendingEventItem = components['schemas']['SendingEventItem'];
export type SendingEventParentType = components['schemas']['SendingEventParentType'];
export type RecordSendingEventRequest = components['schemas']['RecordSendingEventRequest'];
export type SendResponseToPortalRequest = components['schemas']['SendResponseToPortalRequest'];

// Process history types
export type InformationRequestChangeListItem = components['schemas']['InformationRequestChangeListItem'];
export type InformationRequestChangeType = components['schemas']['InformationRequestChangeType'];

// Form labels (LNA-1927) — single source of truth shared with the BO PDF
export type NameDisplayMode = 'Combined' | 'Separate';
export interface NameDisplayLabels {
  firstName: string;
  lastName: string;
  fullName: string;
}
/** A single rendered name row: a caption and its value ('-' when empty). */
export interface NameDisplayRow {
  label: string;
  value: string;
}
export type InformationRequestLabelsResponse = components['schemas']['InformationRequestLabelsResponse'] & {
  enumValues: Record<string, Record<string, string>>;
  // LNA-1927: backend owns whether each person block renders one combined name field or two separate ones.
  nameDisplay: {
    modes: Record<string, NameDisplayMode>;
    labels: NameDisplayLabels;
  };
};
export type LabelSection = components['schemas']['LabelSection'];
export type LabelItem = components['schemas']['LabelItem'];
export type Language = components['schemas']['Language'];

// File object types
export const FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT = 'InformationRequestDocument';
export const FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION = 'InformationRequestAuthorizationDocument';
export const FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT = 'InformationRequestDiscountDocument';
export const FILE_OBJECT_TYPE_INFO_REQUEST_DEATH_CERTIFICATE = 'InformationRequestAuthorizationDocument';

/** The file object type behind each step-2 upload, keyed the way the wizard keys its queued files. */
export const CLIENT_PAYER_FILE_TYPE_BY_KEY: Readonly<Record<string, string>> = {
  jpAuthorization: FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION,
  authorization: FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION,
  deathCertificate: FILE_OBJECT_TYPE_INFO_REQUEST_DEATH_CERTIFICATE,
  discountGroup: FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT
};

// File object type constants for response tab
export const FILE_OBJECT_TYPE_INFO_REQUEST_OTHER_RESPONSE = 'InformationRequestOtherResponseFile';
export const FILE_OBJECT_TYPE_INFO_REQUEST_RESPONSE_DRAFT = 'InformationRequestResponseDraftFile';
export const FILE_OBJECT_TYPE_INFO_REQUEST_NAMEJS_RESPONSE = 'InformationRequestNamejsResponseFile';

// Notes types
export type InformationRequestNoteCard = components['schemas']['InformationRequestNoteCard'];
export type InformationRequestNoteListByInformationRequestIdItem = components['schemas']['InformationRequestNoteListByInformationRequestIdItem'];
export type CreateInformationRequestNoteRequest = components['schemas']['CreateInformationRequestNoteRequest'];
export type UpdateInformationRequestNoteRequest = components['schemas']['UpdateInformationRequestNoteRequest'];

// Communication types
export type BackofficeInformationRequestCommunicationListItem = components['schemas']['BackofficeInformationRequestCommunicationListItem'];
export type BackofficeInformationRequestCommunicationListItemPagedResponse = components['schemas']['BackofficeInformationRequestCommunicationListItemPagedResponse'];
export type BackofficeInformationRequestCommunicationCard = components['schemas']['BackofficeInformationRequestCommunicationCard'];
export type InformationRequestCommunicationType = components['schemas']['InformationRequestCommunicationType'];
export type RequestAdditionalInformationForInformationRequestRequest = components['schemas']['RequestAdditionalInformationForInformationRequestRequest'];
export type LogAdditionalInformationForInformationRequestRequest = components['schemas']['LogAdditionalInformationForInformationRequestRequest'];
export type UpdateLogAdditionalInformationForInformationRequestRequest = components['schemas']['UpdateLogAdditionalInformationForInformationRequestRequest'];
export type SendByEmailForInformationRequestRequest = components['schemas']['SendByEmailForInformationRequestRequest'];
export type SendResponseByEmailForInformationRequestRequest = components['schemas']['SendResponseByEmailForInformationRequestRequest'];

// Co-responsible structural unit types
export type CoResponsibleStructuralUnitItem = components['schemas']['CoResponsibleStructuralUnitItem'];
export type AddCoResponsibleStructuralUnitRequest = components['schemas']['AddCoResponsibleStructuralUnitRequest'];
export type SetConsolidatedRequest = components['schemas']['SetConsolidatedRequest'];

// Task types
export type CreateInformationRequestTaskRequest = components['schemas']['CreateInformationRequestTaskRequest'];
export type InformationRequestTaskCard = components['schemas']['InformationRequestTaskCard'];
export type InformationRequestTaskListItem = components['schemas']['InformationRequestTaskListItem'];
export type InformationRequestTaskListResult = components['schemas']['InformationRequestTaskListResult'];
export type InformationRequestTaskStatus = components['schemas']['InformationRequestTaskStatus'];
export type InformationRequestTaskListScope = components['schemas']['InformationRequestTaskListScope'];
export type InformationRequestTaskStatusCount = components['schemas']['InformationRequestTaskStatusCount'];
export type CancelInformationRequestTaskRequest = components['schemas']['CancelInformationRequestTaskRequest'];

// File object type constants for notes and communication
export const FILE_OBJECT_TYPE_INFO_REQUEST_NOTE = 'InformationRequestNoteFile';
export const FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE = 'InformationRequestCommunicationBackoffice';
export const FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_FRONTOFFICE = 'InformationRequestCommunicationFrontoffice';

// Invoice tab types
export type InformationRequestCalculationCard = components['schemas']['InformationRequestCalculationCard'];
export type InformationRequestCalculationListByInformationRequestIdItem = components['schemas']['InformationRequestCalculationListByInformationRequestIdItem'];
export type CreateInformationRequestCalculationRequest = components['schemas']['CreateInformationRequestCalculationRequest'];
export type UpdateInformationRequestCalculationRequest = components['schemas']['UpdateInformationRequestCalculationRequest'];
export type InformationRequestInvoiceCard = components['schemas']['InformationRequestInvoiceCard'];
export type InformationRequestInvoiceListByInformationRequestIdItem = components['schemas']['InformationRequestInvoiceListByInformationRequestIdItem'];
export type CreateInformationRequestInvoiceRequest = components['schemas']['CreateInformationRequestInvoiceRequest'];
export type UpdateInformationRequestInvoiceRequest = components['schemas']['UpdateInformationRequestInvoiceRequest'];
export type SendInformationRequestInvoiceByEmailRequest = components['schemas']['SendInformationRequestInvoiceByEmailRequest'];
export type SendInformationRequestInvoiceToPortalRequest = components['schemas']['SendInformationRequestInvoiceToPortalRequest'];
export type MarkInformationRequestInvoiceSentRequest = components['schemas']['MarkInformationRequestInvoiceSentRequest'];
export type UpdateInformationRequestPaymentTypesRequest = components['schemas']['UpdateInformationRequestPaymentTypesRequest'];
export type MarkInformationRequestAsPaidRequest = components['schemas']['MarkInformationRequestAsPaidRequest'];

// Invoice tab file object type constants
export const FILE_OBJECT_TYPE_INFO_REQUEST_CALCULATION = 'InformationRequestCalculationFile';
export const FILE_OBJECT_TYPE_INFO_REQUEST_INVOICE = 'InformationRequestInvoiceFile';

// Documents tab types
export type InformationRequestDocumentItem = components['schemas']['InformationRequestDocumentItem'];
export type InformationRequestDocumentFile = components['schemas']['InformationRequestDocumentFile'];
export const FILE_OBJECT_TYPE_INFO_REQUEST_BO_DOCUMENT = 'InformationRequestBoDocument';
